import { useSelector } from 'react-redux';

import { CustomsSectionHeaderContent } from './customs-section-header-content';
import { CustomsDocumentsUploadSubsection } from './customs-documents-upload-subsection';
import { CustomsProvider } from '../../../customs-declarations/context/context';
import { CustomsDeclarationsWidget } from '../../../customs-declarations/customs-declarations-widget';
import { useFulfillmentPlannerCustoms } from '../../../customs-declarations/use-fulfillment-planner-customs';
import Global from '../../../../store/global-selectors';
import { selectCustomsDocumentsCarrierSupportsDocumentUpload } from '../../../../store/customs-documents/customs-documents.selectors';
import { OrdersSidebarSection } from '../orders-sidebar-section';
import sectionStyle from '../orders-sidebar-section.module.less';

/**
 * @param {object} props
 * @param {string} props.defaultCountryCode
 * @param {string} props.defaultCurrencyCode
 * @param {string} props.defaultWeightUnit
 * @param {object} [props.dragHandleProps]
 * @param {string} props.fulfillmentPlanId
 * @param {(open: boolean) => void} props.onClick
 * @param {boolean} props.open
 * @param {boolean} props.readOnly
 * @returns {React.JSX.Element|null}
 */
const CustomsSection = ({
  defaultCountryCode,
  defaultCurrencyCode,
  defaultWeightUnit,
  dragHandleProps,
  fulfillmentPlanId,
  onClick,
  open: isExpanded,
  readOnly,
}) => {
  const { customs, needsCustoms, onSave, hideCustomsDeclarations } = useFulfillmentPlannerCustoms(fulfillmentPlanId);

  const { customsItems = [] } = customs;

  const serviceId = useSelector((state) =>
    fulfillmentPlanId ? Global.selectEntitiesFulfillmentPlanServiceId(state, { fulfillmentPlanId }) : null,
  );
  const carrierApiCode = useSelector((state) => {
    if (!serviceId) return null;
    const service = Global.selectSellerServiceById(state, { serviceId });
    return service?.carrierApiCode ?? null;
  });
  const isCarrierSupported = useSelector((state) =>
    carrierApiCode ? selectCustomsDocumentsCarrierSupportsDocumentUpload(state, carrierApiCode) : true,
  );
  const isCarrierNotSupported = !readOnly && carrierApiCode != null && !isCarrierSupported;

  if (hideCustomsDeclarations || !needsCustoms || !customsItems.length) {
    return null;
  }

  const headerContent = (
    <CustomsSectionHeaderContent customsItems={customsItems} isCarrierNotSupported={isCarrierNotSupported} isReadonly={readOnly} />
  );

  const sectionContent = (
    <CustomsDeclarationsWidget
      className={sectionStyle['order-sidebar-section-default-padding']}
      fulfillmentPlanId={fulfillmentPlanId}
      readOnly={readOnly}
    >
      {!readOnly && <CustomsDocumentsUploadSubsection fulfillmentPlanId={fulfillmentPlanId} />}
    </CustomsDeclarationsWidget>
  );

  return (
    <CustomsProvider
      customs={customs}
      defaultCountryCode={defaultCountryCode}
      defaultCurrencyCode={defaultCurrencyCode}
      defaultWeightUnit={defaultWeightUnit}
      fulfillmentPlanId={fulfillmentPlanId}
      saveCustoms={onSave}
    >
      <OrdersSidebarSection
        dragHandleProps={dragHandleProps}
        headerContent={headerContent}
        isExpanded={isExpanded}
        sectionContent={sectionContent}
        toggleIsExpanded={onClick}
      />
    </CustomsProvider>
  );
};

export const CustomsSectionLoader = (props) => {
  if (!props.shouldShowCustoms) {
    return null;
  }

  return <CustomsSection {...props} />;
};
